import { cva, type VariantProps } from 'class-variance-authority';
import type { LucideIcon } from 'lucide-react';
import * as React from 'react';

import { cn } from '../lib/utils';

const vStatusIconVariants = cva(
  'inline-flex size-7 shrink-0 items-center justify-center rounded-full',
  {
    variants: {
      tone: {
        neutral: 'bg-neutral-100 text-neutral-500',
        warning: 'bg-warning-100 text-warning-600',
        success: 'bg-success-100 text-success-600',
        error: 'bg-error-100 text-error-600',
        ghost: 'text-muted-foreground',
      },
    },
    defaultVariants: {
      tone: 'neutral',
    },
  },
);

type VStatusIconProps = Omit<React.ComponentPropsWithoutRef<'span'>, 'children' | 'aria-label'> &
  VariantProps<typeof vStatusIconVariants> & {
    icon: LucideIcon;
    /** Toegankelijke naam, bv. het statuslabel ("Uitgelezen"). */
    label: string;
    /** Laat het icoon draaien (lopende status); staat stil bij "minder beweging". */
    spin?: boolean;
  };

/**
 * Icon-only statusindicator: rond chipje met een icoon, gekleurd per `tone`.
 * Niet interactief, zodat hij ook binnen een link of knop mag staan; wikkel hem
 * in een tooltip voor de uitleg.
 */
export const VStatusIcon = React.forwardRef<HTMLSpanElement, VStatusIconProps>(
  function VStatusIcon({ icon: Icon, tone, label, spin, className, ...props }, ref) {
    return (
      <span
        ref={ref}
        role="img"
        aria-label={label}
        className={cn(vStatusIconVariants({ tone, className }))}
        {...props}
      >
        <Icon
          size={16}
          aria-hidden
          className={spin ? 'animate-spin motion-reduce:animate-none' : undefined}
        />
      </span>
    );
  },
);
VStatusIcon.displayName = 'VStatusIcon';

export { vStatusIconVariants };
export type { VStatusIconProps };
